import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useFetchWrapper } from '../../_helpers/fetchWrapper.js';
import config from '../../config.js';
import { useT } from '../../contexts/LocaleContext.jsx';
import '../../styles/AutoSuggest.css';

const SongAutoSuggest = ({ 
  placeholder,
  value = "",
  onChange,
  onSongSelect,
  onSubmit,
  className = "",
  showArtist = true,
  disabled = false,
  id,
  testId,
}) => {
  const { bandid } = useParams();
  const t = useT();
  const fetchWrapper = useFetchWrapper();
  const [bandSongs, setBandSongs] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);

  const fetchBandSongs = useCallback(async () => {
    try {
      const response = await fetchWrapper.get(`${config.apiBaseUrl}/bands/${bandid}/songs`);
      
      // Handle new nested API response format
      if (response && response.success && Array.isArray(response.songs)) {
        setBandSongs(response.songs);
      } else if (Array.isArray(response)) {
        // Fallback for old API format
        setBandSongs(response);
      } else {
        console.warn('Unexpected songs response format:', response);
        setBandSongs([]);
      }
    } catch (error) {
      console.error('Error fetching band songs:', error);
      setBandSongs([]);
    }
  }, [fetchWrapper, bandid]);

  // Fetch band songs when component mounts
  useEffect(() => {
    if (bandid) {
      fetchBandSongs();
    }
  }, [bandid, fetchBandSongs]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is on a suggestion item - if so, don't close
      if (event.target.closest('.suggestions-dropdown')) {
        return;
      }
      
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter songs based on input value
  const filterSuggestions = (searchValue) => {
    if (!searchValue.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    const searchLower = searchValue.toLowerCase();
    const filtered = bandSongs
      .filter(song => 
        song.title.toLowerCase().includes(searchLower) ||
        song.artist.toLowerCase().includes(searchLower)
      )
      .slice(0, 10); // Limit to 10 suggestions
    
    setSuggestions(filtered);
    setShowSuggestions(filtered.length > 0);
    setSelectedIndex(-1);
  };

  // Songs that arrive after the member started typing are matched against
  // what is typed (ENG-531): the crate loads on mount, and a title typed
  // before it answered used to show nothing until the next keystroke.
  useEffect(() => {
    if (value && value.trim() && inputRef.current === document.activeElement) {
      filterSuggestions(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- on new songs only
  }, [bandSongs]);

  // Handle suggestion selection
  const selectSuggestion = (song) => {
    console.log('Selecting suggestion:', song);
    if (onSongSelect) {
      onSongSelect(song);
    }
    if (onChange) {
      onChange(song.title);
    }
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    if (onChange) {
      onChange(newValue);
    }
    filterSuggestions(newValue);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) {
      // If no suggestions are showing, handle Enter for submit
      if (e.key === 'Enter' && onSubmit) {
        e.preventDefault();
        onSubmit(value);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const newIndex = selectedIndex < suggestions.length - 1 ? selectedIndex + 1 : 0;
      setSelectedIndex(newIndex);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const newIndex = selectedIndex > 0 ? selectedIndex - 1 : suggestions.length - 1;
      setSelectedIndex(newIndex);
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      selectSuggestion(suggestions[selectedIndex]);
    } else if (e.key === 'Enter' && onSubmit) {
      // If Enter is pressed but no suggestion is selected, perform submit
      e.preventDefault();
      onSubmit(value);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        id={id}
        data-testid={testId}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (value && value.trim()) {
            filterSuggestions(value);
          }
        }}
        placeholder={placeholder ?? t('songList.autoSuggest.placeholder')}
        className={className}
        autoComplete="off"
        disabled={disabled}
      />
      {showSuggestions && suggestions.length > 0 && (
        <div className="suggestions-dropdown">
          {suggestions.map((song, index) => {
            const songId = song.id || song.ID;
            const songTitle = song.title || song.Title;
            const songArtist = song.artist || song.Artist;
            
            return (
              <div
                key={`${songId}-${songTitle}`}
                className={`suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  selectSuggestion(song);
                }}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <div className="suggestion-title">{songTitle}</div>
                {showArtist && (
                  <div className="suggestion-artist">{t('songList.autoSuggest.byArtist', { artist: songArtist })}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SongAutoSuggest;